import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../redux/hooks";
import { useAdvertiserAttributesPoll } from "../../../../../modules/onboarding/walmart/api/useAdvertiserAttributesPoll";
import { PageContainer } from "../../../../../components/core/basic/PageContainer";
import { Center, VStack, HStack } from "../../../../../../styled-system/jsx";
import { css, cx } from "../../../../../../styled-system/css";
import { WalmartProfilesStaffTable } from "../../../../../modules/onboarding/walmart/WalmartProfilesStaffTable";
import Card from "../../../../../components/core/basic/Card";
import { Button, buttonRecipe } from "../../../../../components/core/basic/Button";
import { DevErrorDisplay } from "../../../../../components/core/blocks/DevErrorDisplay";
import { Link, createFileRoute } from "@tanstack/react-router";
import { FaArrowLeft } from "react-icons/fa";

const StaffWalmartProfilesView = () => {
  const { t } = useTranslation(["admin"]);
  const isStaff = useAppSelector((state) => state.user.is_staff);

  const [pollQuery, forceRefresh] = useAdvertiserAttributesPoll({ enabled: isStaff });

  // Extract profiles from successful response
  const profiles = useMemo(() => {
    if (pollQuery.data?.status === "done") {
      return pollQuery.data.unused_advertiser_attributes;
    }
    return [];
  }, [pollQuery.data]);

  if (!isStaff) {
    return (
      <PageContainer>
        <Center gap="medium" flexDirection="column" h="400px">
          <h2>{t(($) => $.admin.staffWalmartProfiles["Access Denied"])}</h2>
          <p>{t(($) => $.admin.staffWalmartProfiles["This page is only accessible to staff members."])}</p>
        </Center>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card display="flex" gap="medium" flexDirection="column" alignItems="stretch">
        <HStack justifyContent="space-between" alignItems="center">
          <Link
            to="/user/walmartsummary"
            className={cx(buttonRecipe({ variant: "link" }), css({ fontSize: "medium" }))}
          >
            <FaArrowLeft />
            <span>{t(($) => $.admin.staffWalmartProfiles["Back to Walmart Onboarding"])}</span>
          </Link>
          <Button
            variant="primary"
            onPress={forceRefresh}
            isDisabled={pollQuery.isFetching}
            aria-label={t(($) => $.admin.staffWalmartProfiles["Force refresh data"])}
          >
            {pollQuery.isFetching ?
              t(($) => $.admin.staffWalmartProfiles["Refreshing..."])
            : t(($) => $.admin.staffWalmartProfiles["Force Refresh"])}
          </Button>
        </HStack>

        <VStack gap="xsmall" alignItems="flex-start">
          <h1 className={css({ margin: 0 })}>
            {t(($) => $.admin.staffWalmartProfiles["Walmart Advertiser Profiles"])}
          </h1>
          {pollQuery.data?.status === "done" && (
            <p className={css({ m: "0", color: "text.secondary", fontSize: "small" })}>
              {t(($) => $.admin.staffWalmartProfiles["Snapshot ID: {snapshotId} | Updated: {updatedOn}"], {
                snapshotId: pollQuery.data.snapshot_id,
                updatedOn: new Date(pollQuery.data.updated_on).toLocaleString(),
              })}
            </p>
          )}
        </VStack>

        {pollQuery.isLoading && (
          <VStack gap="small" alignItems="center" py="xxlarge">
            <p className={css({ m: "0", fontSize: "xlarge", fontWeight: "semibold" })}>
              {t(($) => $.admin.staffWalmartProfiles["Retrieving advertiser profiles..."])}
            </p>
            <p className={css({ m: "0", color: "grey" })}>
              {t(
                ($) =>
                  $.admin.staffWalmartProfiles[
                    "Please be patient, this can potentially take a few minutes. You're free to navigate away and come back later."
                  ],
              )}
            </p>
          </VStack>
        )}

        {pollQuery.isError && (
          <DevErrorDisplay
            userMessage={t(($) => $.admin.staffWalmartProfiles["Error loading profiles"])}
            error={pollQuery.error}
          />
        )}

        {pollQuery.data?.status === "done" && (
          <>
            <VStack gap="small" alignItems="flex-start">
              <h2 className={css({ m: "0", color: "text.heading", fontSize: "xxlarge", fontWeight: "bold" })}>
                {t(($) => $.admin.staffWalmartProfiles["Unused Profiles ({count})"], { count: profiles.length })}
              </h2>
              <p className={css({ m: "0", color: "text.secondary" })}>
                {t(
                  ($) =>
                    $.admin.staffWalmartProfiles[
                      "These advertiser IDs are available to connect but not yet associated with any organization."
                    ],
                )}
              </p>
            </VStack>
            {profiles.length > 0 ?
              <WalmartProfilesStaffTable profiles={profiles} />
            : <p className={css({ m: "0", color: "grey", fontStyle: "italic" })}>
                {t(($) => $.admin.staffWalmartProfiles["No unused profiles available"])}
              </p>
            }
          </>
        )}
      </Card>
    </PageContainer>
  );
};

export const Route = createFileRoute("/_authenticated/user/staff/walmart/profiles")({
  component: StaffWalmartProfilesView,
  staticData: { application: "Admin" },
});
